import { GetServerSideProps } from "next";
import Head from "next/head";
import ClaimCheck from "@/core/components/ClaimCheck";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Brand, Domains, Locales } from "@/core/types/types";
import CamundaService from "../../server/service/CamundaService";
import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";
import { BackupTranslations } from "@/core/translations/translations";
import { initialiseApp } from "server/intitialiseApp";
import getDomain from "@/core/utils/getDomain";
import FlowStateManager from "@/core/utils/FlowStateManager";
import { setUser } from "@/core/lib/datadog/datadog";
import getBrandConfiguration, {
  getDomainData,
} from "@/core/utils/getBrandConfiguration";

type HomeProps = {
  initialisationConfig: InitialisationConfig;
};

export default function Home({ initialisationConfig }: Readonly<HomeProps>) {
  const brandId: Brand = initialisationConfig?.brandId || Brand.DEFAULT;
  const logoPath = getBrandConfiguration(brandId).openGraphLogoSources;
  const tld: string =
    initialisationConfig?.domain === Domains.UK
      ? `co.${initialisationConfig?.domain}`
      : initialisationConfig?.domain;
  const logoSrc = `${
    getBrandConfiguration(brandId).mainLogoUrls
  }${tld}/${logoPath}`;
  return (
    <>
      <Head>
        <title>{t("opengraph.title")}</title>
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t("opengraph.title")} />
        <meta property="og:description" content={t("opengraph.description")} />
        <meta property="og:image" content={logoSrc} />
        <meta property="og:image:width" content="400" />
        <meta property="og:image:height" content="300" />
        <meta name="twitter:title" content={t("opengraph.title")} />
        <meta name="twitter:description" content={t("opengraph.description")} />
        <meta name="twitter:image" content={logoSrc} />
        <meta property="twitter:image:width" content="400" />
        <meta property="twitter:image:height" content="300" />
        <meta name="description" content={t("opengraph.description")} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        <script
          src="https://cdn.eu.amplitude.com/script/4eeaf1dec2cb35151640372d6701fd67.experiment.js"
          async
        />
      </Head>
      <ClaimCheck initConfig={initialisationConfig} />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    req: {
      cookies: { process: currentProcess },
      headers: { host },
    },
    query: { processinstanceid: process },
  } = ctx;

  const initialisationConfig: InitialisationConfig = await initialiseApp(ctx);
  if (process) {
    initialisationConfig.processId = process as string;
  } else {
    initialisationConfig.processId = FlowStateManager.fromCookie(
      ctx.req,
      ctx.res
    ).getProcessIdForFlow(initialisationConfig.definitionName);
  }

  if (initialisationConfig.processId !== "") {
    setUser(initialisationConfig.processId);
  }

  const { locale, translationTenant, legalArea } = initialisationConfig;

  const sourceDomain = host && getDomain(host);
  const isAlive: boolean = await CamundaService.isClaimCheckAlive(
    initialisationConfig.legalArea,
    sourceDomain
  );

  if (!isAlive) {
    const brandFamily: string = initialisationConfig.brandFamily;
    const domain: Domains = initialisationConfig.domain;
    const backupURL: string | undefined = getDomainData(
      brandFamily,
      legalArea,
      domain
    ).BackupClaimCheckData?.url;

    const backupTranslations =
      BackupTranslations[locale] || BackupTranslations[Locales.US];

    // Process not defined: redirect to backup CC
    // Process defined: show error screen with backup CC
    if (!process && !currentProcess) {
      console.warn("###### Redirecting new user", backupURL);
      return {
        redirect: {
          destination: backupURL,
          permanent: false,
        },
        props: {},
      };
    } else {
      initialisationConfig.forceShowBackupCC = true;

      return {
        props: {
          translations: backupTranslations,
          host,
          initialisationConfig,
        },
      };
    }
  }

  return {
    props: {
      translations: await CamundaService.getTranslations(
        locale,
        translationTenant,
        legalArea,
        initialisationConfig.language,
        sourceDomain
      ),
      host: ctx.req.headers.host,
      initialisationConfig,
    },
  };
};
